<script setup lang="ts">
import { computed, ref } from 'vue'
import { CustomMarker, InfoWindow } from 'vue3-google-map'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import LaHausVerifiedLogo from '@lahaus-roomie/roomie/src/assets/icons/v2/la-haus-verificado-logo.svg'
import AppMap from '@/components/BrandedV2/AppMap/index.vue'
import SearchResultProjectCard from '@/components/BrandedV2/SearchResultProjectCard/index.vue'
import type { SearchResultMapComponentModule } from '@/components/BrandedV2/SearchResultMap/types'
import type { InvestmentProjectSummary } from '@/server/api/buyer-front/types'
import { MAP_MARKER_POSITION_LIST } from '@/components/BrandedV2/AppMap/enum'

const props = withDefaults(defineProps<SearchResultMapComponentModule.Props>(), {
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  },
  outstandingProject: null
})

const { $useLhId } = useNuxtApp()
const { public: { searchResultPage } } = useRuntimeConfig()
const googleMapsApiKey = searchResultPage.googleMapsApiKey
const appMap = ref()
const projectSelected = ref<InvestmentProjectSummary | null>()

const mapCoordinatesCenter = computed(() => {
  return {
    latitude: projectsToRender.value[0].latitude,
    longitude: projectsToRender.value[0].longitude
  }
})

const infoWindowOptions = computed(() => {
  return {
    anchorPoint: MAP_MARKER_POSITION_LIST.TOP_CENTER,
    position: mapCoordinatesToLatLngBounds(projectSelected.value.latitude, projectSelected.value.longitude)
  }
})

const projectsToRender = computed(() => {
  const projects = [...props.projects]
  if (props.outstandingProject) {
    projects.unshift(props.outstandingProject)
  }

  return projects
})

const mapCoordinatesToLatLngBounds = (latitude: number, longitude: number) => {
  return {
    lat: latitude,
    lng: longitude
  }
}

const showProjectCardDetail = (event: MouseEvent, project: InvestmentProjectSummary) => {
  event.stopPropagation()
  projectSelected.value = project
}

const hideProjectCardDetail = () => {
  projectSelected.value = null
}

const onProjectCardClick = async (project: InvestmentProjectSummary) => {
  const { path } = project
  await navigateTo(path, {
    open: {
      target: '_blank'
    }
  })

  hideProjectCardDetail()
}
</script>

<script lang="ts">
export default {
  name: 'SearchResultMap'
}
</script>

<template>
  <div :class="['search-result-map w-full h-full relative lg:z-0', { 'z-20': !!projectSelected}]">
    <AppMap
      ref="appMap"
      :api-key="googleMapsApiKey"
      :map-coordinates-center="mapCoordinatesCenter"
      :zoom="12"
      :zoom-control="true"
      @click="hideProjectCardDetail"
      @dragend="hideProjectCardDetail()">
      <template #customMarker>
        <CustomMarker
          v-for="(project, index) in projectsToRender"
          :key="project.code"
          :options="{ position: mapCoordinatesToLatLngBounds(project.latitude, project.longitude), anchorPoint: 'BOTTOM_CENTER' }"
          @click="(event) => showProjectCardDetail(event, project)">
          <RoomieChipSelector
            :id="`${project.code}-tag-selector`"
            :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'ver', C: project.name, I: index})"
            :is-selected="project.code === projectSelected?.code"
            variant="pine">
            <template #content>
              <div class="flex flex-row gap-8">
                <RoomieIcon custom-styles="block w-20 h-20">
                  <LaHausVerifiedLogo />
                </RoomieIcon>
                {{ project.name }}
              </div>
            </template>
          </RoomieChipSelector>

          <InfoWindow
            v-if="projectSelected && projectSelected.code === project.code"
            :options="infoWindowOptions"
            @closeclick="hideProjectCardDetail">
            <SearchResultProjectCard
              class="cursor-pointer"
              :project="projectSelected"
              :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'redireccionar', C: project.name, I: index, CPRI: project.code})"
              @click="onProjectCardClick" />
          </InfoWindow>
        </CustomMarker>
      </template>
    </AppMap>
  </div>
</template>

<style lang="scss">
.search-result-map {
  .gm-style-iw.gm-style-iw-c {
    @apply rounded-xl p-0 overflow-hidden bg-white #{!important};
  }

  .gm-style-iw-d {
    @apply rounded-xl p-0 overflow-hidden bg-transparent #{!important};
  }

  .gm-style-iw-chr {
    @apply bg-transparent h-0 z-10 #{!important};

    & button.gm-ui-hover-effect {
      @apply flex justify-center outline outline-2 outline-pine-600 w-[3.4rem] h-[3.4rem] bg-white rounded-full top-7 right-7 #{!important};

      transition: all 0.2s linear;

      & span {
        @apply w-[2rem] h-[2rem] text-pine-600 bg-pine-600 m-8 #{!important};
      }
    }
  }

  .gm-style-iw-t {
    @apply bottom-[6rem] #{!important};
  }

  .gm-style-iw-tc {
    @apply hidden;
  }
}
</style>
